import { Fragment } from 'react';

import { useTranslation } from 'react-i18next';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { Box, Chip, Collapse, IconButton, List, ListItem, Stack, Tooltip, Typography } from '@mui/material';

import { formatNumber } from '../../lib/format';
import type { CategoryTreeNode } from '../../model/types';

export interface CategoryTreeViewProps {
  nodes: CategoryTreeNode[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  ancestorNamesById: Map<string, string[]>;
  productCountById: Map<string, number>;
  canManage: boolean;
  onEdit: (id: string) => void;
  onDelete: (category: { id: string; name: string }) => void;
  onViewHistory: (category: { id: string; name: string }) => void;
}

/**
 * Hand-rolled expandable tree (MUI `List`/`Collapse` — no tree-view dependency) for the tenant's
 * full category hierarchy (`GET /catalog/categories/tree`, epic-catalog-005,
 * user-story-catalog-011). Each row indents by depth, shows the resolved product count (if
 * available from the flat search index) and, for deeper nodes, the ancestor breadcrumb.
 */
export function CategoryTreeView({
  nodes,
  expandedIds,
  onToggle,
  ancestorNamesById,
  productCountById,
  canManage,
  onEdit,
  onDelete,
  onViewHistory,
}: CategoryTreeViewProps) {
  const { t } = useTranslation('catalog');

  const renderNode = (node: CategoryTreeNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const ancestors = ancestorNamesById.get(node.id) ?? [];
    const productCount = productCountById.get(node.id);

    return (
      <Fragment key={node.id}>
        <ListItem disablePadding sx={{ pl: depth * 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', py: 0.75, pr: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => onToggle(node.id)}
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded
                    ? t('categories.rowActions.collapseAria', { name: node.name })
                    : t('categories.rowActions.expandAria', { name: node.name })
                }
                sx={{ mt: 0.25 }}
              >
                {isExpanded ? (
                  <ExpandMoreRoundedIcon fontSize="small" />
                ) : (
                  <ChevronRightRoundedIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 32, height: 32, flexShrink: 0 }} aria-hidden />
            )}

            <Box sx={{ flex: 1, minWidth: 0, ml: 0.5, mt: 0.25 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600}>
                  {node.name}
                </Typography>
                {node.localeFallback ? (
                  <Tooltip title={t('categories.localeFallbackTooltip')}>
                    <Chip size="small" variant="outlined" label={t('categories.localeFallbackChip')} />
                  </Tooltip>
                ) : null}
                {productCount !== undefined ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('categories.table.productCountChip', { count: formatNumber(productCount) })}
                  />
                ) : null}
              </Stack>
              {ancestors.length > 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                  {ancestors.join(' / ')}
                </Typography>
              ) : null}
            </Box>

            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Tooltip title={t('categories.rowActions.history')}>
                <IconButton
                  size="small"
                  aria-label={t('categories.rowActions.historyAria', { name: node.name })}
                  onClick={() => onViewHistory({ id: node.id, name: node.name })}
                >
                  <HistoryRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={canManage ? t('categories.rowActions.edit') : t('categories.rowActions.view')}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={t('categories.rowActions.editAria', { name: node.name })}
                  onClick={() => onEdit(node.id)}
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canManage ? (
                <Tooltip title={t('categories.rowActions.delete')}>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('categories.rowActions.deleteAria', { name: node.name })}
                    onClick={() => onDelete({ id: node.id, name: node.name })}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Box>
        </ListItem>
        {hasChildren ? (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>{node.children.map((child) => renderNode(child, depth + 1))}</List>
          </Collapse>
        ) : null}
      </Fragment>
    );
  };

  return (
    <List disablePadding aria-label={t('categories.treeAriaLabel')}>
      {nodes.map((node) => renderNode(node, 0))}
    </List>
  );
}
